import { MAX_CHARS_POLICY_NUMBER, SPAIN_PREFIX } from '@/modules/core/constants/common'

export const validateNIF = (dni: string) => {
  if (!validDNIFormat(dni)) return false

  const numbers = dni.slice(0, -1)
  const character = dni.slice(-1)

  const expectedCharacter = calculateDNILetter(numbers)
  return expectedCharacter === character.toLocaleUpperCase()
}

export const validateNIE = (nie: string) => {
  const valid = /^[XYZ]{1}[0-9]{7}[A-Z]{1}$/.test(nie)
  if (!valid) return false

  const firstCharacter = nie.slice(0, 1)
  const firstNumber = { X: 0, Y: 1, Z: 2 }[firstCharacter]
  const numbers = `${firstNumber}${nie.slice(1, -1)}`
  const character = nie.slice(-1)

  return validateNIF(`${numbers}${character}`)
}

export const validateCIF = (cif: string) => {
  return /^[A-Za-z]\d{8}$/.test(cif)
}

export const validateName = (name: string) => {
  if (!name) return true
  return /^[a-zA-ZÀ-ÿ\s'´-]+$/.test(name)
}

/**
 * Validates a name string against a more complex set of rules.
 * regex101 link with tests: https://regex101.com/r/9RpzGd/7
 * TODO: this should replace validateName
 * @returns boolean
 */
export const validateNameV2 = (name: string, blockedNames: string) => {
  if (!name) return true
  const validChars = 'a-zA-ZáÁéÉíÍóÓúÚäÄëËïÏöÖüÜñÑçÇÀàÈèÌìÒòÙùÃãÕõ'
  return (
    new RegExp(
      `^[${validChars}]{2,}(?:(?:-[${validChars}]{2,})*(?:\\s[${validChars}]{2,}(?:-[${validChars}]{2,})*){0,4})$`
    ).test(name) && !new RegExp(`\\b(${blockedNames})\\b`, 'i').test(name.toUpperCase())
  )
}

export const validDNIFormat = (dni: string) => {
  return /^\d{8}[A-Za-z]$/.test(dni)
}

export const validPassportFormat = (passport: string) => {
  // const limit = MAX_CHARS_PASSPORT
  const regex = new RegExp(`^[a-zA-Z0-9]+$`)
  return regex.test(passport)
}

export const validPolicyNumberFormat = (policyNumber: string) => {
  const limit = MAX_CHARS_POLICY_NUMBER
  const regex = new RegExp(`^[0-9]{1,${limit}}$`)
  return regex.test(policyNumber)
}

export const validatePostalCode = (postalCode: string) => {
  return /^\d{5}$/.test(postalCode)
}

export const isCPFromNavarra = (postalCode: string) => {
  if (postalCode.startsWith('31')) {
    return true
  }
  return false
}

export const validatePhone = (phone: string, countryPrefix = SPAIN_PREFIX) => {
  if (countryPrefix === SPAIN_PREFIX) {
    return /^[6-9]{1}\d{8}$/.test(phone)
  }

  return /^(\d){6,15}$/.test(phone)
}

export const validateMobile = (phone: string, countryPrefix = SPAIN_PREFIX) => {
  if (countryPrefix === SPAIN_PREFIX) {
    return /^[67]{1}\d{8}$/.test(phone)
  }

  return /^(\d){6,11}$/.test(phone)
}

export const validateEmail = (email: string) => {
  const EMAIL_REGEX = /^(?!.*\.\.)[a-zA-Z0-9._+-]{1,64}(?<!\.)@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return EMAIL_REGEX.test(email)
}

export const validMonth = (month: string | number) => {
  return /^(0?[1-9]|1[0-2])$/.test(String(month))
}

const calculateDNILetter = (dni: string) => {
  const chars = 'TRWAGMYFPDXBNJZSQVHLCKET'
  const intDNI = parseInt(dni)
  const index = intDNI % (chars.length - 1)
  return chars[index]
}

export const isLeapYear = (year: number) => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export const isNumber = (value: string) => {
  return /\d+/.test(value)
}

/**
 * Checks if a date is valid.  (also checks for leap years)
 * Accepts input in dd/MM/yyyy string format or as a Date object.
 * @param value string | Date
 * @returns boolean
 */
export const validDate = (value: string | Date): boolean => {
  let dia: number, mes: number, anio: number

  if (typeof value === 'string') {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/
    const match = value.match(regex)

    if (!match) {
      return false // Formato inválido
    }

    dia = parseInt(match[1], 10)
    mes = parseInt(match[2], 10)
    anio = parseInt(match[3], 10)
  } else if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      return false // Fecha inválida
    }

    dia = value.getDate()
    mes = value.getMonth() + 1 // Meses en Date van de 0 a 11
    anio = value.getFullYear()
  } else {
    return false // Tipo no soportado
  }

  // Validar el rango de mes (1-12)
  if (mes < 1 || mes > 12) {
    return false
  }

  // Validar el rango de día para el mes y considerar años bisiestos
  const diasPorMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  // Ajustar febrero para años bisiestos
  if (mes === 2 && isLeapYear(anio)) {
    diasPorMes[1] = 29
  }

  // Validar el rango de día para el mes
  if (dia < 1 || dia > diasPorMes[mes - 1]) {
    return false
  }

  return true
}

export const validateActuarialDate = (birthDate: Date, minAge: number, maxAge: number) => {
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()

  // Next birthday in the current year
  const nextBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate())

  // Time difference between today and the next birthday
  const diffMs = nextBirthday.getTime() - today.getTime()

  // Approximate 6 months in milliseconds
  const sixMonthsInMs = 6 * 30.4375 * 24 * 60 * 60 * 1000

  // If the next birthday is within 6 months (past or future), round up the age
  if (Math.abs(diffMs) <= sixMonthsInMs) {
    age += 1
  }

  return age >= minAge || age <= maxAge
}
